import { FC } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { QuestionnaireScore } from '@stores/QuestionnaireAdminStore';
import { arrangeScores, scoreLabel, severityBandColor, severityBandLabel } from './questionnaireResultsFormatting';

interface ScoreProps {
  score: QuestionnaireScore;
  emphasised?: boolean;
}

// A label, then the number, then its band as a coloured chip, e.g. "PHQ-4 total  6  [Moderate]". A score that only
// records an answer, e.g. "General health  [Very good]", shows the answer without its coding number.
const Score: FC<ScoreProps> = ({ score, emphasised = false }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Typography component="span" variant="caption" color="text.secondary">
      {scoreLabel(score)}
    </Typography>
    {!score.answerOnly && (
      <Typography component="span" variant="body2" sx={{ fontWeight: emphasised ? 700 : 500 }}>
        {score.value}
      </Typography>
    )}
    {score.severityBand && (
      <Chip
        size="small"
        label={severityBandLabel(score.severityBand)}
        color={severityBandColor(score.severityBand)}
        variant={emphasised ? 'filled' : 'outlined'}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    )}
  </Box>
);

const ScoreLine: FC<{ scores: QuestionnaireScore[] }> = ({ scores }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}>
    {scores.map((score) => (
      <Score key={`${score.instrumentCode}-${score.scaleCode}`} score={score} />
    ))}
  </Box>
);

/**
 * Each instrument's total on one line, bold, with its subscales on the line below. One-question instruments share a
 * line at the end.
 */
export const ScoreSummary: FC<{ scores: QuestionnaireScore[] }> = ({ scores }) => {
  if (!scores.length) return <>—</>;
  const { detailed, singles } = arrangeScores(scores);

  return (
    // minWidth 0 lets it shrink inside a grid cell, so scores wrap rather than run past the edge.
    <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
      {detailed.map((group) => (
        <Stack key={group.instrumentCode ?? 'questionnaire'} spacing={0.25}>
          {group.total && <Score score={group.total} emphasised />}
          <ScoreLine scores={group.subscales} />
        </Stack>
      ))}
      {singles.length > 0 && <ScoreLine scores={singles} />}
    </Stack>
  );
};
